import { evaluateCondition, readPath } from "../dsl/expression.js";
import { resolveFrameEntries } from "./entity-frame.js";
import { synthesizeGrid } from "./synthesize-grid.js";
import { effectsToCss, fillToCss, paintToCss } from "./resolve-fill.js";
export function renderNode(node, context) {
    const visible = evaluateCondition(node.visibleIf, context.scope);
    if (!visible && !context.showHiddenNodes)
        return null;
    const rendered = renderNodeInner(node, context);
    if (!rendered)
        return null;
    const stamped = withDefaultInstance(rendered);
    if (visible)
        return stamped;
    return {
        ...stamped,
        style: {
            ...(stamped.style ?? {}),
            opacity: 0.35,
            outline: '1px dashed rgba(255,64,129,0.8)',
            outlineOffset: -1,
        },
    };
}
/** Unique ids on every painted clone so the constructor can hit-test copies. */
export function stampInstanceTree(node, suffix) {
    const instanceId = `${node.sourceNodeId ?? node.id}${suffix}`;
    return {
        ...node,
        id: `${node.id}${suffix}`,
        instanceId,
        children: node.children?.map((child, index) => stampInstanceTree(child, `${suffix}.${index}`)),
    };
}
function withDefaultInstance(node) {
    if (node.instanceId) {
        return {
            ...node,
            children: node.children?.map(withDefaultInstance),
        };
    }
    return {
        ...node,
        instanceId: node.id,
        children: node.children?.map(withDefaultInstance),
    };
}
function renderNodeInner(node, context) {
    switch (node.kind) {
        case 'stack':
            return renderStack(node, context);
        case 'grid':
            return renderGrid(node, context);
        case 'group':
            return renderGroup(node, context);
        case 'frame':
            return renderFrame(node, context);
        case 'repeater':
            return renderRepeater(node, context);
        case 'slot':
            return node.defaultChild ? renderNode(node.defaultChild, context) : null;
        case 'text':
            return renderText(node, context);
        case 'image':
            return renderImage(node, context);
        case 'shape':
            return renderShape(node, context);
        case 'divider':
            return renderDivider(node, context);
        case 'spacer':
            return renderSpacer(node, context);
        case 'overlay':
            return renderOverlay(node, context);
        default:
            return null;
    }
}
// --- Containers ------------------------------------------------------------
function frameToCss(frame) {
    if (!frame)
        return undefined;
    return {
        position: 'absolute',
        boxSizing: 'border-box',
        left: `${frame.x * 100}%`,
        top: `${frame.y * 100}%`,
        width: `${frame.width * 100}%`,
        height: `${frame.height * 100}%`,
        transform: frame.rotate ? `rotate(${frame.rotate}deg)` : undefined,
        zIndex: frame.z ?? 1,
    };
}
function childFrameMode(children) {
    const framed = children.some((child) => child.frame);
    const flow = children.some((child) => !child.frame);
    if (framed && flow)
        return 'mixed';
    if (framed)
        return 'freeform';
    return 'flow';
}
function renderStack(node, context) {
    const isRow = node.direction === 'row';
    const childWidth = isRow
        ? Math.max(80, context.availableWidth / Math.max(1, node.children.length))
        : context.availableWidth;
    const children = renderChildren(node.children, { ...context, availableWidth: childWidth });
    const frameMode = childFrameMode(node.children);
    const useFlex = frameMode !== 'freeform';
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'box',
        style: {
            display: useFlex ? 'flex' : 'block',
            flexDirection: useFlex ? (isRow ? 'row' : 'column') : undefined,
            gap: useFlex ? px(resolveLengthValue(node.gap, context)) : undefined,
            alignItems: useFlex ? alignToCss(node.align, isRow) : undefined,
            justifyContent: useFlex ? justifyToCss(node.justify) : undefined,
            flexWrap: useFlex ? (node.wrap ? 'wrap' : undefined) : undefined,
            position: frameMode === 'flow' ? undefined : 'relative',
            width: frameMode === 'freeform' ? '100%' : undefined,
            minHeight: frameMode === 'freeform' ? '100%' : undefined,
            minWidth: 0,
            ...boxStyleToCss(node.style, context),
            ...layoutToCss(node.layout, context),
            ...frameToCss(node.frame),
        },
        children,
    };
}
function renderFrame(node, context) {
    const alignCss = contentAlignToFlexCss(node);
    const isBox = Boolean(alignCss.display);
    const frameStyle = {
        display: isBox ? 'flex' : 'contents',
        pointerEvents: 'none',
        boxSizing: isBox ? 'border-box' : undefined,
        minWidth: isBox ? 0 : undefined,
        minHeight: isBox ? 0 : undefined,
        ...alignCss,
        ...boxStyleToCss(node.style, context),
        ...layoutToCss(node.layout, context),
    };
    const iteration = resolveFrameEntries(node, {
        scope: context.scope,
        pageGroup: context.pageGroup,
        menuFormat: context.menuFormat,
    });
    if (!iteration.iterate) {
        return {
            id: node.id,
            sourceNodeId: node.id,
            tag: 'box',
            style: frameStyle,
            children: renderChildren(node.children, context),
        };
    }
    if (iteration.entries.length === 0)
        return null;
    const children = [];
    iteration.entries.forEach((entry, index) => {
        const heading = sectionHeadingOf(entry);
        if (heading) {
            children.push(renderSectionHeading(heading, context, `${node.id}__heading_${index}`));
        }
        const childContext = {
            ...context,
            scope: { ...context.scope, [iteration.as]: entry, index },
        };
        collectItemId(entry, context);
        for (const child of renderChildren(node.children, childContext)) {
            children.push({
                ...stampInstanceTree(child, `__${index}`),
                dataRef: dataRefFor(entry) ?? child.dataRef,
            });
        }
    });
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'box',
        style: frameStyle,
        children,
    };
}
function renderGroup(node, context) {
    const alignCss = contentAlignToFlexCss(node);
    const isBox = Boolean(alignCss.display);
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'box',
        style: {
            display: isBox ? 'flex' : 'contents',
            pointerEvents: 'none',
            boxSizing: isBox ? 'border-box' : undefined,
            minWidth: isBox ? 0 : undefined,
            minHeight: isBox ? 0 : undefined,
            ...alignCss,
            ...boxStyleToCss(node.style, context),
            ...layoutToCss(node.layout, context),
        },
        children: renderChildren(node.children, context),
    };
}
function renderGrid(node, context) {
    // A grid whose only child is a repeater is the interesting case: the item
    // count is data-driven, so the tiling can be synthesised from the content.
    const repeaterChild = node.children.find((child) => child.kind === 'repeater');
    if (node.synthesize && repeaterChild && node.children.length === 1) {
        return renderSynthesizedGrid(node, repeaterChild, context);
    }
    const columns = node.columns;
    const children = renderChildren(node.children, {
        ...context,
        availableWidth: Math.max(80, context.availableWidth / Math.max(1, columns)),
    });
    const frameMode = childFrameMode(node.children);
    const useGrid = frameMode !== 'freeform';
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'box',
        style: {
            display: useGrid ? 'grid' : 'block',
            gridTemplateColumns: useGrid ? `repeat(${columns}, minmax(0, 1fr))` : undefined,
            gap: useGrid ? px(resolveLengthValue(node.gap, context)) : undefined,
            columnGap: useGrid
                ? node.columnGap
                    ? px(resolveLengthValue(node.columnGap, context))
                    : undefined
                : undefined,
            rowGap: useGrid
                ? node.rowGap
                    ? px(resolveLengthValue(node.rowGap, context))
                    : undefined
                : undefined,
            gridAutoRows: useGrid
                ? node.rowHeight === 'auto' || node.rowHeight == null
                    ? 'minmax(0, auto)'
                    : px(resolveLengthValue(node.rowHeight, context))
                : undefined,
            position: frameMode === 'flow' ? undefined : 'relative',
            width: frameMode === 'freeform' ? '100%' : undefined,
            minHeight: frameMode === 'freeform' ? '100%' : undefined,
            ...contentAlignToGridCss(node),
            ...boxStyleToCss(node.style, context),
            ...layoutToCss(node.layout, context),
            ...frameToCss(node.frame),
        },
        children,
    };
}
function renderSynthesizedGrid(node, repeater, context) {
    const entries = resolveRepeaterEntries(repeater, context);
    const hints = entries.map((entry) => {
        const record = entry;
        return {
            featured: Boolean(record.featured),
            hasImage: Boolean(record.image),
            imageAspect: typeof record.imageAspect === 'number' ? record.imageAspect : undefined,
            textLength: String(record.name ?? '').length + String(record.description ?? '').length,
        };
    });
    const placement = synthesizeGrid(node.synthesize, hints, context.prng.fork(`grid:${node.id}`));
    const childWidth = Math.max(80, context.availableWidth / placement.columns);
    const children = [];
    let extraRows = 0;
    entries.forEach((entry, index) => {
        const cell = placement.cells[index];
        const childContext = {
            ...context,
            scope: { ...context.scope, [repeater.as]: entry },
            availableWidth: cell ? childWidth * cell.colSpan : childWidth,
        };
        const heading = sectionHeadingOf(entry);
        if (heading && cell) {
            if (cell.colStart > 1)
                extraRows += 1;
            children.push(renderSectionHeading(heading, context, `${repeater.id}__heading_${index}`, {
                gridColumn: '1 / -1',
                gridRow: String(cell.rowStart + extraRows),
            }));
            extraRows += 1;
        }
        const rendered = renderNode(repeater.itemTemplate, childContext);
        if (!rendered)
            return;
        collectItemId(entry, context);
        children.push({
            ...stampInstanceTree(rendered, `__${index}`),
            dataRef: dataRefFor(entry),
            style: {
                ...rendered.style,
                gridColumn: cell ? `${cell.colStart} / span ${cell.colSpan}` : undefined,
                gridRow: cell ? `${cell.rowStart + extraRows} / span ${cell.rowSpan}` : undefined,
            },
        });
    });
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'box',
        style: {
            display: 'grid',
            gridTemplateColumns: `repeat(${placement.columns}, minmax(0, 1fr))`,
            gap: px(resolveLengthValue(node.gap, context)),
            gridAutoRows: node.rowHeight === 'auto' || node.rowHeight === undefined
                ? 'minmax(0, auto)'
                : px(resolveLengthValue(node.rowHeight, context)),
            ...contentAlignToGridCss(node),
            ...boxStyleToCss(node.style, context),
            ...layoutToCss(node.layout, context),
        },
        children,
    };
}
function ghostRepeaterEntries(node) {
    const asCategory = node.as === 'category' || node.source.includes('categories');
    if (asCategory) {
        return [
            { id: 'ghost-cat-1', name: 'Starters', description: 'To begin', itemCount: 6 },
            { id: 'ghost-cat-2', name: 'Mains', description: 'From the fire', itemCount: 8 },
        ];
    }
    return [
        {
            id: 'ghost-1',
            name: 'Sample dish',
            description: 'Preview so you can judge the rhythm',
            price: 1850,
            portion: '125 ml',
            tags: [],
            allergens: [],
            featured: false,
            available: true,
            position: 0,
        },
        {
            id: 'ghost-2',
            name: 'Another dish',
            description: 'Name and price stay aligned',
            price: 2400,
            portion: '180 g',
            tags: [],
            allergens: [],
            featured: false,
            available: true,
            position: 1,
        },
        {
            id: 'ghost-3',
            name: 'Third dish',
            description: '',
            price: 1200,
            portion: '75 ml',
            tags: [],
            allergens: [],
            featured: false,
            available: true,
            position: 2,
        },
    ];
}
function renderRepeater(node, context) {
    let entries = resolveRepeaterEntries(node, context);
    let ghosting = false;
    if (entries.length === 0) {
        if (context.ghostEmptyRepeaters) {
            entries = ghostRepeaterEntries(node);
            ghosting = true;
        }
        else {
            return node.empty ? renderNode(node.empty, context) : null;
        }
    }
    const children = [];
    entries.forEach((entry, index) => {
        const heading = sectionHeadingOf(entry);
        if (heading) {
            children.push(renderSectionHeading(heading, context, `${node.id}__heading_${index}`));
        }
        const rendered = renderNode(node.itemTemplate, {
            ...context,
            scope: { ...context.scope, [node.as]: entry, index },
        });
        if (!rendered)
            return;
        if (!ghosting)
            collectItemId(entry, context);
        children.push({
            ...stampInstanceTree(rendered, `__${index}`),
            dataRef: dataRefFor(entry),
        });
    });
    if (ghosting) {
        return {
            id: node.id,
            sourceNodeId: node.id,
            tag: 'box',
            style: {
                display: 'flex',
                flexDirection: 'column',
                gap: px(8 * (context.tokens.duScale ?? 1)),
                opacity: 0.85,
                outline: '1px dashed rgba(139, 92, 246, 0.55)',
                outlineOffset: 4,
                pointerEvents: 'auto',
                ...layoutToCss(node.layout, context),
            },
            children,
        };
    }
    // Prefer `display: contents` so parent gap/align apply to each item. When the
    // repeater itself must flex-grow (header + list + footer columns), that would
    // discard layout — use a real column box instead so footers stay pinned.
    if (repeaterNeedsLayoutBox(node)) {
        return {
            id: node.id,
            sourceNodeId: node.id,
            tag: 'box',
            style: {
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                minHeight: 0,
                pointerEvents: 'auto',
                ...layoutToCss(node.layout, context),
            },
            children,
        };
    }
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'box',
        style: { display: 'contents', pointerEvents: 'auto' },
        children,
    };
}
/** True when the repeater's own box must exist for flex/size layout to work. */
function repeaterNeedsLayoutBox(node) {
    const layout = node.layout;
    if (!layout)
        return false;
    return (layout.grow != null ||
        layout.shrink != null ||
        layout.basis != null ||
        layout.height != null ||
        layout.minHeight != null ||
        layout.maxHeight != null ||
        layout.width != null ||
        layout.minWidth != null ||
        layout.maxWidth != null);
}
function resolveRepeaterEntries(node, context) {
    const source = readPath(context.scope, node.source);
    if (!Array.isArray(source))
        return [];
    let entries = [...source];
    if (node.filter) {
        entries = entries.filter((entry) => evaluateCondition(node.filter, { ...context.scope, [node.as]: entry }));
    }
    if (node.sortBy) {
        const direction = node.sortDirection === 'desc' ? -1 : 1;
        entries.sort((a, b) => {
            const left = readPath(a, node.sortBy);
            const right = readPath(b, node.sortBy);
            if (typeof left === 'number' && typeof right === 'number')
                return (left - right) * direction;
            return String(left ?? '').localeCompare(String(right ?? '')) * direction;
        });
    }
    const offset = node.offset ?? 0;
    return node.limit === undefined
        ? entries.slice(offset)
        : entries.slice(offset, offset + node.limit);
}
// --- Leaves ----------------------------------------------------------------
function renderText(node, context) {
    const token = context.tokens.typography[node.typography];
    if (!token)
        return null;
    let value = interpolate(node.source.template, context.scope);
    if (!value.trim() && node.source.fallback) {
        value = interpolate(node.source.fallback, context.scope);
    }
    if (!value.trim())
        return null;
    value = applyFormat(value, node.source.format, context);
    const fontSize = node.autoFit
        ? autoFitSize(value, token, node.maxLines ?? 2, context.availableWidth)
        : token.size;
    if (node.autoFit && fontSize < token.size * 0.9) {
        context.diagnostics.push({
            level: 'info',
            code: 'text-truncated',
            message: `Shrank "${truncate(value, 32)}" from ${token.size} to ${fontSize} units to fit`,
            pageIndex: context.pageIndex,
            nodeId: node.id,
        });
    }
    const textAlign = textAlignFromNode(node);
    const clamp = node.maxLines
        ? {
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: node.maxLines,
            overflow: 'hidden',
        }
        : {};
    const alignCss = textContentAlignToCss(node);
    const typeCss = {
        ...typographyToCss(token, fontSize),
        color: colorOf(node.color, context),
        textAlign,
    };
    const isPrice = node.source.format === 'price' || node.typography === 'price';
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'text',
        text: value,
        dataBound: node.source.template.includes('{{'),
        style: {
            minWidth: 0,
            ...typeCss,
            ...(alignCss.display === 'flex' ? {} : clamp),
            ...alignCss,
            ...boxStyleToCss(node.style, context),
            ...layoutToCss(node.layout, context),
            // After layoutToCss — it writes `flexShrink: undefined` for unset
            // layout.shrink and would clobber an earlier 0.
            ...(isPrice ? { flexShrink: 0, whiteSpace: 'nowrap' } : {}),
        },
        // Line-clamp uses `-webkit-box`, which cannot pack a short string inside a
        // tall frame. The renderer paints this on an inner span so the outer box
        // can stay `display:flex`.
        textStyle: alignCss.display === 'flex'
            ? {
                ...typeCss,
                width: '100%',
                ...(node.maxLines ? clamp : { display: 'block' }),
            }
            : undefined,
    };
}
function renderImage(node, context) {
    const bound = node.source.binding ? readPath(context.scope, node.source.binding) : undefined;
    const assetUrl = node.source.assetId ? context.assets.get(node.source.assetId) : undefined;
    const src = typeof bound === 'string' && bound ? bound : (assetUrl ?? null);
    const placeholder = node.source.placeholder ?? 'pattern';
    if (!src && placeholder === 'none')
        return null;
    if (!src) {
        context.diagnostics.push({
            level: 'info',
            code: 'image-missing',
            message: `No image for "${node.source.binding ?? node.source.assetId ?? node.id}"`,
            pageIndex: context.pageIndex,
            nodeId: node.id,
        });
    }
    const altSource = readPath(context.scope, 'item.name') ?? readPath(context.scope, 'category.name');
    const alt = typeof altSource === 'string' ? altSource : '';
    const focus = imageFocusCss(node);
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'image',
        image: {
            src,
            alt,
            placeholder,
            initials: initialsOf(alt),
        },
        style: {
            objectFit: node.fit,
            aspectRatio: node.aspect ? String(node.aspect) : undefined,
            filter: node.grayscale ? 'grayscale(1)' : undefined,
            // Carried as custom properties so the renderer can lay the tint over the
            // picture without a second element.
            '--mv-overlay-color': node.overlay ? colorOf(node.overlay.color, context) : undefined,
            '--mv-overlay-opacity': node.overlay ? node.overlay.opacity : undefined,
            '--mv-focus-x': focus.x,
            '--mv-focus-y': focus.y,
            ...boxStyleToCss(node.style, context),
            ...layoutToCss(node.layout, context),
        },
    };
}
function renderShape(node, context) {
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'shape',
        shape: node.shape,
        style: {
            ...paintToCss(node.fill, context),
            borderRadius: node.shape === 'ellipse' ? '50%' : undefined,
            border: node.stroke
                ? `${node.stroke.width * (context.tokens.duScale ?? 1)}px solid ${colorOf(node.stroke.color, context)}`
                : undefined,
            ...boxStyleToCss(node.style, context),
            ...layoutToCss(node.layout, context),
        },
    };
}
function renderDivider(node, context) {
    const color = colorOf(node.color, context);
    const thickness = (node.thickness ?? 1) * (context.tokens.duScale ?? 1);
    const horizontal = node.orientation === 'horizontal';
    // The leader variant is the row of dots between a dish and its price. A
    // repeating radial gradient keeps the dots evenly spaced whatever the gap.
    const leader = node.variant === 'leader';
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'divider',
        style: {
            alignSelf: horizontal ? undefined : 'stretch',
            width: horizontal ? '100%' : px(thickness),
            height: horizontal ? (leader ? px(Math.max(thickness, 2 * (context.tokens.duScale ?? 1))) : px(thickness)) : '100%',
            backgroundColor: leader ? undefined : color,
            backgroundImage: leader
                ? `radial-gradient(circle, ${color} ${context.tokens.duScale ?? 1}px, transparent ${1.2 * (context.tokens.duScale ?? 1)}px)`
                : undefined,
            backgroundSize: leader ? `${6 * (context.tokens.duScale ?? 1)}px 100%` : undefined,
            backgroundRepeat: leader ? 'repeat-x' : undefined,
            backgroundPosition: leader ? 'left bottom' : undefined,
            opacity: leader ? 0.7 : undefined,
            borderStyle: node.variant === 'dashed' || node.variant === 'dotted' ? node.variant : undefined,
            ...boxStyleToCss(node.style, context),
            ...layoutToCss(node.layout, context),
            // Leaders sit in a name——price flex row. width:100% (above) would make
            // that row wider than a grid column and spill the price into the
            // neighbouring cell ($10Burrata Plate). Override after layoutToCss —
            // it writes `width: undefined` for unset layout.width and would clobber
            // an earlier `0`.
            ...(leader && horizontal ? { width: 0, minWidth: 0 } : {}),
        },
    };
}
function renderSpacer(node, context) {
    const size = node.size === undefined ? undefined : resolveLengthValue(node.size, context);
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'box',
        style: size === undefined ? { flexGrow: 1 } : { flexShrink: 0, height: px(size) },
    };
}
function renderOverlay(node, context) {
    const children = [];
    for (const child of node.children) {
        const rendered = renderNode(child.node, {
            ...context,
            availableWidth: Math.max(40, context.availableWidth * child.width),
        });
        if (!rendered)
            continue;
        children.push({
            ...rendered,
            style: {
                ...rendered.style,
                position: 'absolute',
                boxSizing: 'border-box',
                left: `${child.x * 100}%`,
                top: `${child.y * 100}%`,
                width: `${child.width * 100}%`,
                height: `${child.height * 100}%`,
                transform: child.rotate ? `rotate(${child.rotate}deg)` : undefined,
                zIndex: child.z ?? 1,
            },
        });
    }
    return {
        id: node.id,
        sourceNodeId: node.id,
        tag: 'box',
        style: {
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            ...boxStyleToCss(node.style, context),
        },
        children,
    };
}
// --- Style resolution ------------------------------------------------------
function boxStyleToCss(style, context) {
    if (!style)
        return {};
    const { tokens } = context;
    const border = style.border ? tokens.borders[style.border] : undefined;
    const legacyShadow = style.shadow ? tokens.shadows[style.shadow] : undefined;
    const radius = typeof style.radius === 'number'
        ? style.radius * (tokens.duScale ?? 1)
        : style.radius
            ? (tokens.radii[style.radius] ?? 0)
            : undefined;
    const transforms = [];
    if (style.rotate)
        transforms.push(`rotate(${style.rotate}deg)`);
    const fillCss = style.fill
        ? fillToCss(style.fill, context)
        : style.background
            ? { backgroundColor: colorOf(style.background, context) }
            : {};
    const effectCss = effectsToCss(style.effects, context);
    const legacyShadowCss = legacyShadow
        ? {
            boxShadow: `${legacyShadow.offsetX}px ${legacyShadow.offsetY}px ${legacyShadow.blur}px ${legacyShadow.spread}px ${withAlpha(colorOf(legacyShadow.color, context), legacyShadow.opacity)}`,
        }
        : {};
    const mergedShadow = effectCss.boxShadow && legacyShadowCss.boxShadow
        ? `${legacyShadowCss.boxShadow}, ${effectCss.boxShadow}`
        : (effectCss.boxShadow ?? legacyShadowCss.boxShadow);
    return {
        ...fillCss,
        border: border
            ? `${border.width}px ${border.style} ${colorOf(border.color, context)}`
            : undefined,
        borderRadius: radius === undefined ? undefined : px(radius),
        boxShadow: mergedShadow,
        filter: effectCss.filter,
        padding: edgeInsetsToCss(style.padding, context),
        margin: edgeInsetsToCss(style.margin, context),
        opacity: style.opacity,
        transform: transforms.length > 0 ? transforms.join(' ') : undefined,
        overflow: style.clip ? 'hidden' : undefined,
    };
}
function layoutToCss(layout, context) {
    if (!layout)
        return {};
    const dimension = (value) => {
        if (value === undefined)
            return undefined;
        if (value === 'auto')
            return 'auto';
        if (value === 'full')
            return '100%';
        return px(resolveLengthValue(value, context));
    };
    return {
        flexGrow: layout.grow,
        flexShrink: layout.shrink,
        flexBasis: layout.basis === undefined ? undefined : px(resolveLengthValue(layout.basis, context)),
        width: dimension(layout.width),
        height: dimension(layout.height),
        minHeight: layout.minHeight === undefined
            ? undefined
            : px(resolveLengthValue(layout.minHeight, context)),
        maxHeight: layout.maxHeight === undefined
            ? undefined
            : px(resolveLengthValue(layout.maxHeight, context)),
        minWidth: layout.minWidth === undefined
            ? undefined
            : px(resolveLengthValue(layout.minWidth, context)),
        maxWidth: layout.maxWidth === undefined
            ? undefined
            : px(resolveLengthValue(layout.maxWidth, context)),
        alignSelf: layout.alignSelf ? alignToCss(layout.alignSelf, false) : undefined,
        gridColumn: layout.colSpan ? `span ${layout.colSpan}` : undefined,
        gridRow: layout.rowSpan ? `span ${layout.rowSpan}` : undefined,
        order: layout.order,
    };
}
function typographyToCss(token, fontSize) {
    return {
        fontFamily: `var(--mv-font-${token.role})`,
        fontSize: px(fontSize),
        fontWeight: token.weight,
        lineHeight: token.lineHeight,
        letterSpacing: token.letterSpacing === undefined ? undefined : `${token.letterSpacing}em`,
        textTransform: token.textTransform === 'none' ? undefined : token.textTransform,
        fontStyle: token.italic ? 'italic' : undefined,
    };
}
function edgeInsetsToCss(insets, context) {
    if (!insets)
        return undefined;
    const value = (side) => side === undefined ? '0' : px(resolveLengthValue(side, context));
    return `${value(insets.top)} ${value(insets.right)} ${value(insets.bottom)} ${value(insets.left)}`;
}
function resolveLengthValue(value, context) {
    // JSON/Mongo often persist cleared lengths as `null`; treat like omitted.
    if (value == null)
        return 0;
    if (typeof value === 'number')
        return value * (context.tokens.duScale ?? 1);
    return context.tokens.spacing[value.slice(1)] ?? 0;
}
function colorOf(ref, context) {
    return context.tokens.colors[ref] ?? ref;
}
/** Appends an alpha channel to a 6-digit hex, which shadows need. */
function withAlpha(hex, opacity) {
    const alpha = Math.round(Math.max(0, Math.min(1, opacity)) * 255)
        .toString(16)
        .padStart(2, '0');
    return /^#[0-9a-fA-F]{6}$/.test(hex) ? `${hex}${alpha}` : hex;
}
// --- Text ------------------------------------------------------------------
const PLACEHOLDER = /\{\{\s*([\w.]+)\s*\}\}/g;
function interpolate(template, scope) {
    return template.replace(PLACEHOLDER, (_match, path) => {
        const value = readPath(scope, path);
        if (value === null || value === undefined)
            return '';
        if (typeof value === 'number')
            return String(value);
        if (typeof value === 'boolean')
            return value ? 'yes' : '';
        if (Array.isArray(value))
            return value.join(', ');
        if (typeof value === 'object')
            return '';
        return String(value);
    });
}
function applyFormat(value, format, context) {
    switch (format) {
        case 'price': {
            const amount = Number(value.replace(/[^\d.-]/g, ''));
            if (!Number.isFinite(amount))
                return value;
            try {
                return new Intl.NumberFormat(context.locale, {
                    style: 'currency',
                    currency: context.currency,
                    // Whole-unit prices read better without ".00" on a menu.
                    minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
                    maximumFractionDigits: 2,
                }).format(amount / 100);
            }
            catch {
                return (amount / 100).toFixed(2);
            }
        }
        case 'uppercase':
            return value.toUpperCase();
        case 'lowercase':
            return value.toLowerCase();
        case 'capitalize':
            return value.replace(/\b\w/g, (char) => char.toUpperCase());
        case 'ordinal': {
            const number = Number(value);
            if (!Number.isFinite(number))
                return value;
            const suffix = ['th', 'st', 'nd', 'rd'][(number % 100) - 20] ??
                ['th', 'st', 'nd', 'rd'][number % 100] ??
                'th';
            return `${number}${suffix}`;
        }
        default:
            return value;
    }
}
/**
 * Estimated shrink-to-fit.
 *
 * Real autofit needs glyph metrics, which the server does not have and which
 * would make the layout differ between server and browser anyway. The estimate
 * uses an average advance width of 0.52em - close enough across the serif and
 * sans faces the templates ship with - and the renderer still applies a line
 * clamp as a hard backstop.
 */
function autoFitSize(text, token, maxLines, availableWidth) {
    const minSize = token.minSize ?? token.size * 0.75;
    const maxSize = token.maxSize ?? token.size;
    if (availableWidth <= 0)
        return maxSize;
    const averageAdvance = 0.52;
    const charsPerLine = Math.max(1, availableWidth / (maxSize * averageAdvance));
    const capacity = charsPerLine * maxLines;
    if (text.length <= capacity)
        return maxSize;
    // Area scales with the square of the size, so the correction is a square root.
    const scaled = maxSize * Math.sqrt(capacity / text.length);
    return Math.max(minSize, Math.round(scaled * 10) / 10);
}
function truncate(value, length) {
    return value.length <= length ? value : `${value.slice(0, length - 1)}…`;
}
function initialsOf(value) {
    return value
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('');
}
// --- Misc ------------------------------------------------------------------
function renderChildren(children, context) {
    const rendered = [];
    for (const child of children) {
        const node = renderNode(child, context);
        if (!node)
            continue;
        const frameCss = frameToCss(child.frame);
        rendered.push(frameCss ? { ...node, style: { ...node.style, ...frameCss } } : node);
    }
    return rendered;
}
function dataRefFor(entry) {
    if (!entry || typeof entry !== 'object')
        return undefined;
    const record = entry;
    if (typeof record.id !== 'string')
        return undefined;
    // Only menu items carry a price, and they are the only thing worth tracking
    // taps on.
    return typeof record.price === 'number'
        ? { kind: 'item', id: record.id }
        : { kind: 'category', id: record.id };
}
function sectionHeadingOf(entry) {
    if (!entry || typeof entry !== 'object')
        return undefined;
    const value = entry.sectionHeading;
    return typeof value === 'string' && value.trim() ? value : undefined;
}
function renderSectionHeading(text, context, id, placement = {}) {
    const token = context.tokens.typography.sectionTitle ??
        context.tokens.typography.display ??
        context.tokens.typography.itemName;
    const color = colorOf('onSurface', context);
    return {
        id,
        sourceNodeId: id,
        tag: 'text',
        text,
        style: {
            fontFamily: token ? `var(--mv-font-${token.role})` : undefined,
            fontSize: token ? `${token.size}px` : '22px',
            fontWeight: token?.weight ?? 600,
            lineHeight: token?.lineHeight ?? 1.1,
            letterSpacing: token?.letterSpacing === undefined ? undefined : `${token.letterSpacing}em`,
            color,
            gridColumn: placement.gridColumn ?? '1 / -1',
            gridRow: placement.gridRow,
            paddingTop: '8px',
            paddingBottom: '4px',
        },
    };
}
function collectItemId(entry, context) {
    if (!entry || typeof entry !== 'object')
        return;
    const record = entry;
    if (typeof record.id === 'string' && typeof record.price === 'number') {
        if (!context.itemIds.includes(record.id))
            context.itemIds.push(record.id);
    }
}
function alignToCss(align, isRow) {
    switch (align) {
        case 'start':
            return 'flex-start';
        case 'center':
            return 'center';
        case 'end':
            return 'flex-end';
        case 'stretch':
            return 'stretch';
        case 'baseline':
            // Baseline alignment across a column is meaningless and browsers treat it
            // as flex-start anyway; being explicit avoids surprising authors.
            return isRow ? 'baseline' : 'flex-start';
        default:
            return undefined;
    }
}
function justifyToCss(justify) {
    switch (justify) {
        case 'start':
            return 'flex-start';
        case 'center':
            return 'center';
        case 'end':
            return 'flex-end';
        case 'between':
            return 'space-between';
        case 'around':
            return 'space-around';
        case 'evenly':
            return 'space-evenly';
        default:
            return undefined;
    }
}
function hasContentAlign(node) {
    return Boolean(node.contentAlignX || node.contentAlignY);
}
/**
 * Pack children inside a box. Column flex so X is align-items and Y is
 * justify-content — the same mental model as the inspector's two-axis control.
 */
function contentAlignToFlexCss(node) {
    if (!hasContentAlign(node))
        return {};
    return {
        display: 'flex',
        flexDirection: 'column',
        alignItems: alignToCss(node.contentAlignX ?? 'stretch', false),
        justifyContent: justifyToCss(node.contentAlignY ?? 'start'),
    };
}
function contentAlignToGridCss(node) {
    if (!hasContentAlign(node))
        return {};
    return {
        justifyItems: alignToCss(node.contentAlignX ?? 'stretch', false),
        alignItems: alignToCss(node.contentAlignY ?? 'start', false),
    };
}
function textAlignFromNode(node) {
    if (node.align)
        return node.align;
    switch (node.contentAlignX) {
        case 'center':
            return 'center';
        case 'end':
            return 'right';
        default:
            return undefined;
    }
}
function textContentAlignToCss(node) {
    const y = node.contentAlignY;
    if (!y)
        return {};
    return {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: justifyToCss(y),
        alignItems: 'stretch',
    };
}
function imageFocusCss(node) {
    const x = node.focus !== undefined
        ? `${node.focus.x * 100}%`
        : contentAlignToPercent(node.contentAlignX);
    const y = node.focus !== undefined
        ? `${node.focus.y * 100}%`
        : contentAlignToPercent(node.contentAlignY);
    return { x, y };
}
function contentAlignToPercent(align) {
    if (align === 'start')
        return '0%';
    if (align === 'end')
        return '100%';
    if (align === 'center')
        return '50%';
    return undefined;
}
function px(value) {
    return `${value}px`;
}
//# sourceMappingURL=render-nodes.js.map